import { z } from 'zod'
import {
  BindModeSchema,
  CoordsSchema,
  FrameworkSchema,
  FRAMEWORKS,
  BIND_MODES,
  type BindMode,
  type Coords,
  type Framework,
} from './constants.js'
import { DetectionKeySchema, SeveritySchema, ActionSchema, SideSchema } from './detections.js'
import { IdentifiersSchema, TokensSchema, primaryIdentifier } from './identifiers.js'
import { ErrorCodeSchema } from './errors.js'

/**
 * SPEC.md §4.3 / AGENT-PROTOCOL.md §5 — the frozen resource ↔ cloud wire contract.
 *
 * Every schema here is parsed on both ends: the API validates agent requests and
 * serialises the responses, the Lua agent (`resource/sentinel_ac/server/*.lua`)
 * reads the same named keys. Field names are frozen — `playerSource` (never
 * `source`) for an in-game server id, `detectionKey` for the enum, `detectionId`
 * for a row uuid, `eventId` for the agent's idempotency id, and `agentBanCode`
 * (provisional, minted offline by the agent) vs `banCode` (canonical, cloud-minted).
 *
 * Bounds are deliberately tight: the agent is a semi-trusted party running on
 * someone else's machine, so every string, array and integer that crosses the
 * wire is capped. Transport, signing, MAC and rate limits are §5.1 / §5.13 and
 * live in the API plugins, not here.
 */

/* -------------------------------------------------------------------------- */
/* Primitives                                                                 */
/* -------------------------------------------------------------------------- */

/** Wire timestamps are always ISO-8601 UTC strings (`2026-09-07T12:00:28.000Z`). */
const isoDateTime = z.iso.datetime()

/** Row ids are Postgres uuids (`detections.id`, `bans.id`, `commands.id`, …). */
const uuid = z.uuid()

/** `1.0.0` — compared against `MIN_AGENT_VERSION` by semver, §5.2. */
const semver = z
  .string()
  .max(32)
  .regex(/^\d{1,5}\.\d{1,5}\.\d{1,5}(?:[-+][0-9A-Za-z.-]{1,32})?$/, 'expected a semver string')

/** ULID-ish boot id minted on every start (`01J8XK4`, SPEC §8.3). */
const bootId = z.string().min(1).max(32)

/** `bootId .. '-' .. seq` — the agent-side idempotency key (SPEC §8.6). */
const agentEventId = z.string().min(1).max(64)

/** `servers.config_version` / `servers.ban_list_version`; the latter is a bigint. */
const versionNumber = z.number().int().min(0).max(Number.MAX_SAFE_INTEGER)

/** In-game server id from `source`. `sv_maxclients` caps at 2048; ids are sparse. */
const playerSource = z.number().int().min(1).max(65535)

/** Display name from `GetPlayerName`; §5.6 rejects anything over 128 chars. */
const playerName = z.string().max(128)

/** Cloud `SAC-` + 8 Crockford base32, or the agent's provisional `SAC-L…` (SPEC §8.7). */
const banCode = z
  .string()
  .min(5)
  .max(32)
  .regex(/^SAC-[0-9A-Z]{4,24}$/, 'expected a SAC- ban code')

/** IPv4 or IPv6 literal, already stripped of the `:port` `GetPlayerEndpoint` adds. */
const ipAddress = z.string().min(3).max(45)

/** sha256 hex — config checksums and the global ban list's identifier hashes. */
const sha256Hex = z.string().regex(/^[0-9a-f]{64}$/, 'expected 64 lowercase hex chars')

/** Capability URLs handed to a player's NUI; host comes from `PUBLIC_API_URL`. */
const httpUrl = z.url().max(512)

/** `general.appealUrl` is either a URL or the empty string (config.ts §4.2). */
const appealUrl = z.union([z.url().max(512), z.literal('')])

/** Framework detected by `server/framework.lua`; mirrors `general.framework`. */

/**
 * Free-form JSON blobs (`evidence`, `samples[]`, command `result`) are opaque to
 * the schema, so they are bounded by serialised size instead of by shape — a
 * hostile agent must not be able to spend the whole 256 KB body on one field.
 */
const jsonSizeAtMost =
  (maxChars: number) =>
  (value: unknown): boolean => {
    try {
      const encoded = JSON.stringify(value)
      return typeof encoded === 'string' && encoded.length <= maxChars
    } catch {
      return false
    }
  }

/** Per-event evidence budget; the hard limit is `policy.detectionMaxBytes` (§5.5). */
const MAX_EVIDENCE_CHARS = 8192
/** `shared/strike.lua` caps `samples` at 12 entries; this caps their total weight. */
const MAX_SAMPLES_CHARS = 8192
/** `commands.result` is a small display blob (`{ "kicked": true }`, §5.9.2). */
const MAX_COMMAND_RESULT_CHARS = 2048

const evidenceObject = z
  .record(z.string(), z.unknown())
  .refine(jsonSizeAtMost(MAX_EVIDENCE_CHARS), { message: `evidence exceeds ${MAX_EVIDENCE_CHARS} JSON chars` })

/** GTA V world coordinates. Bounded so a spoofed `1e308` cannot reach the DB. */

/* -------------------------------------------------------------------------- */
/* §5.2 POST /v1/agent/auth                                                    */
/* -------------------------------------------------------------------------- */

/**
 * §5.2 — license key exchange. Signed with the key carried in this same body
 * (there is no JWT yet), so the API must derive `mac_key` from `licenseKey`
 * before it trusts anything here.
 */
export const AgentAuthRequestSchema = z.object({
  /** `sacl_<keyId>_<secret><crc>` (SPEC §10.1); CRC is checked before any DB access. */
  licenseKey: z
    .string()
    .min(20)
    .max(128)
    .regex(/^sacl_[0-9A-Za-z]{6,16}_[0-9A-Za-z]{20,96}$/, 'expected a sacl_ license key'),
  agentVersion: semver,
  /** `PROTOCOL_VERSION` from constants.ts (currently 1); §5.11 negotiates it. */
  protocolVersion: z.number().int().min(1).max(1000),
  bootId,
  /** Random 32 hex, created once in `cache/instance.json` and kept across restarts. */
  instanceId: z.string().regex(/^[0-9a-f]{32}$/, 'expected 32 lowercase hex chars'),
  serverName: z.string().min(1).max(128),
  maxPlayers: z.number().int().min(1).max(2048),
  framework: FrameworkSchema,
  /** `GetConvar('sv_enforceGameBuild')` and `GetConvar('version')`, logged only. */
  gameBuild: z.string().max(16),
  fxVersion: z.string().max(16),
  resourceCount: z.number().int().min(0).max(5000),
  /** `fnv1a:<hex>` over the sorted started-resource list; drives `resource_list_mismatch`. */
  resourceHash: z.string().max(64),
})
export type AgentAuthRequest = z.infer<typeof AgentAuthRequestSchema>

/**
 * §5.2 — the tuning the cloud pushes to the agent. Every value has a canonical
 * default, so an older API that omits the whole object still yields a working
 * agent. Bounds match the rate limits and body caps of §5.13.
 */
export const AgentPolicySchema = z.object({
  protocolVersion: z.number().int().min(1).max(1000).default(1),
  minAgentVersion: semver.default('1.0.0'),
  /** 6 heartbeats/min is the hard rate limit, so 10 000 ms is the floor (§5.3). */
  heartbeatIntervalMs: z.number().int().min(10000).max(300000).default(30000),
  detectionFlushMs: z.number().int().min(1000).max(60000).default(5000),
  /** `events.length > 100` is `413 BATCH_TOO_LARGE` (§5.5). */
  detectionMaxBatch: z.number().int().min(1).max(100).default(100),
  /** 256 KB — the API's body limit for `/v1/agent/detections`. */
  detectionMaxBytes: z.number().int().min(16384).max(262144).default(262144),
  sessionFlushMs: z.number().int().min(1000).max(120000).default(15000),
  /** `events.length > 200` is `413 BATCH_TOO_LARGE` (§5.7). */
  sessionMaxBatch: z.number().int().min(1).max(200).default(200),
  /** Total disk-queue budget across bans/detections/sessions/acks (§5.11). */
  maxQueueEvents: z.number().int().min(100).max(100000).default(5000),
  /** `net.lua`'s own watchdog; `PerformHttpRequest` has no total-duration timeout. */
  requestTimeoutMs: z.number().int().min(1000).max(60000).default(15000),
  backoffBaseMs: z.number().int().min(100).max(60000).default(1000),
  backoffMaxMs: z.number().int().min(1000).max(600000).default(60000),
  circuitOpenAfterFailures: z.number().int().min(1).max(100).default(5),
  circuitCooldownMs: z.number().int().min(1000).max(600000).default(60000),
  /** The API rejects `|now − X-SAC-Timestamp|` beyond this with `AGENT_SIGNATURE_INVALID`. */
  clockSkewToleranceSec: z.number().int().min(30).max(3600).default(300),
})
export type AgentPolicy = z.infer<typeof AgentPolicySchema>

/** `licenses.bind_mode` (SPEC §3.1, §10.1); `none` forces `logOnly` above 8 slots. */

/**
 * §5.2 — `200` response, MAC'd. `serverTime` seeds the agent's `clockOffsetSec`;
 * `configVersion`/`banListVersion` tell it whether to fetch §5.4 and §5.8.3 at boot.
 */
export const AgentAuthResponseSchema = z.object({
  /** EdDSA agent JWT, 15 minutes; claims in SPEC §10.2. */
  token: z.string().min(1).max(4096),
  expiresIn: z.number().int().min(60).max(86400),
  serverId: uuid,
  serverName: z.string().max(128),
  bindMode: BindModeSchema,
  /** True while the *superseded* key of a rotation is being presented (§5.2). */
  licenseRotated: z.boolean().default(false),
  /** End of the 24 h rotation grace; the new key is never returned over this channel. */
  graceUntil: isoDateTime.nullable().default(null),
  serverTime: isoDateTime,
  configVersion: versionNumber,
  banListVersion: versionNumber,
  policy: AgentPolicySchema,
})
export type AgentAuthResponse = z.infer<typeof AgentAuthResponseSchema>

/* -------------------------------------------------------------------------- */
/* §5.4 GET /v1/agent/config                                                   */
/* -------------------------------------------------------------------------- */

/** §5.4 — `?since=<version>`; a non-integer value is `400 VALIDATION_ERROR`. */
export const AgentConfigQuerySchema = z.object({
  since: z.coerce.number().int().min(0).max(Number.MAX_SAFE_INTEGER).optional(),
})
export type AgentConfigQuery = z.infer<typeof AgentConfigQuerySchema>

/**
 * §5.4 — the active `ServerConfig`, MAC'd. A real `304` is deliberately not used
 * because it carries no body and therefore no verifiable MAC; `changed: false`
 * with no `config` key gives the same saving with integrity intact.
 *
 * `config` is typed loosely here on purpose: `ServerConfigSchema` lives in
 * `config.ts` and is the authority that the API parses with before serialising.
 */
export const ConfigResponseSchema = z.object({
  version: versionNumber,
  /** Cloud sha256 over canonical JSON; the agent stores it verbatim as a cache tag. */
  checksum: sha256Hex,
  /** `ConfigSync.apply` refuses anything but 1. */
  schemaVersion: z.literal(1),
  changed: z.boolean(),
  generatedAt: isoDateTime,
  config: z.record(z.string(), z.unknown()).optional(),
})
export type ConfigResponse = z.infer<typeof ConfigResponseSchema>

/* -------------------------------------------------------------------------- */
/* §5.9 Commands                                                               */
/* -------------------------------------------------------------------------- */

/** `command_type` (SPEC §3.1). `ban`/`unban` are minted only by the bans service. */
export const AgentCommandTypeSchema = z.enum(['kick', 'ban', 'unban', 'screenshot', 'message', 'reloadConfig'])
export type AgentCommandType = z.infer<typeof AgentCommandTypeSchema>

const commandEnvelope = {
  id: uuid,
  issuedAt: isoDateTime,
  /** 10 minutes from creation; past it the row is `expired` and never re-delivered. */
  expiresAt: isoDateTime,
}

const kickCommand = z.object({
  ...commandEnvelope,
  type: z.literal('kick'),
  payload: z.object({ playerSource, reason: z.string().max(500) }),
})

const banCommand = z.object({
  ...commandEnvelope,
  type: z.literal('ban'),
  payload: z.object({
    playerSource,
    banCode,
    reason: z.string().max(500),
    /** 0 = permanent; one year is the ceiling enforced on `POST /v1/agent/bans`. */
    durationSec: z.number().int().min(0).max(31536000).default(0),
    expiresAt: isoDateTime.nullable().default(null),
  }),
})

const unbanCommand = z.object({
  ...commandEnvelope,
  type: z.literal('unban'),
  payload: z.object({ banCode }),
})

const screenshotCommand = z.object({
  ...commandEnvelope,
  type: z.literal('screenshot'),
  payload: z.object({ playerSource, screenshotId: uuid, uploadUrl: httpUrl, expiresAt: isoDateTime }),
})

const messageCommand = z.object({
  ...commandEnvelope,
  type: z.literal('message'),
  /** `playerSource: -1` broadcasts to every player (§5.9.1). */
  payload: z.object({ playerSource: z.number().int().min(-1).max(65535), text: z.string().max(500) }),
})

const reloadConfigCommand = z.object({
  ...commandEnvelope,
  type: z.literal('reloadConfig'),
  payload: z.object({}),
})

/**
 * §5.9.1 — delivered in `HeartbeatResponse.commands[]`, at most 20 per beat.
 * Delivery is at-least-once: the agent keeps the last 200 executed ids and
 * re-sends the stored ack instead of executing a re-delivered command twice.
 */
export const AgentCommandSchema = z.discriminatedUnion('type', [
  kickCommand,
  banCommand,
  unbanCommand,
  screenshotCommand,
  messageCommand,
  reloadConfigCommand,
])
export type AgentCommand = z.infer<typeof AgentCommandSchema>

/**
 * §5.9.2 — one executed command. `queued`, `delivered` and `expired` are
 * cloud-side states and are never reported by the agent.
 */
export const CommandAckResultSchema = z.object({
  commandId: uuid,
  status: z.enum(['acked', 'failed']),
  /** Small display blob stored in `commands.result` (`{ "kicked": true }`). */
  result: z
    .record(z.string(), z.unknown())
    .refine(jsonSizeAtMost(MAX_COMMAND_RESULT_CHARS), { message: 'result too large' })
    .nullable()
    .default(null),
  /** Short machine token: `player_not_online`, `ban_not_found`, `screenshot_timeout`, `expired`. */
  error: z.string().max(64).nullable().default(null),
  executedAt: isoDateTime,
})
export type CommandAckResult = z.infer<typeof CommandAckResultSchema>

/** §5.9.2 — `POST /v1/agent/commands/ack`; an empty or >50-entry batch is a `400`. */
export const CommandAckRequestSchema = z.object({
  results: z.array(CommandAckResultSchema).min(1).max(50),
})
export type CommandAckRequest = z.infer<typeof CommandAckRequestSchema>

/** §5.9.2 — the inline ack response (no MAC). */
export const CommandAckResponseSchema = z.object({
  acked: z.number().int().min(0).max(50),
  failed: z.number().int().min(0).max(50),
  /** Ids this server has never been issued. */
  unknown: z.array(uuid).max(50).default([]),
  /** Ids past `commands.expires_at`; the agent drops them from its queue. */
  expired: z.array(uuid).max(50).default([]),
})
export type CommandAckResponse = z.infer<typeof CommandAckResponseSchema>

/* -------------------------------------------------------------------------- */
/* §5.3 POST /v1/agent/heartbeat                                               */
/* -------------------------------------------------------------------------- */

/**
 * §5.3 / SPEC §8.11 — one coalesced bypass-export call, keyed by
 * `(export, resource, playerSource, moduleKey)`. Each entry becomes an
 * `audit_logs` row with `actor_type: 'agent'`.
 */
export const BypassEventSchema = z.object({
  /** The bypass export another resource invoked (`AllowWeapon`, `IgnorePlayer`). */
  export: z.string().min(1).max(64),
  resource: z.string().min(1).max(64),
  playerSource,
  /** A `DetectionKey`, or `*` when the bypass covered every module. */
  moduleKey: z.union([DetectionKeySchema, z.literal('*')]),
  seconds: z.number().int().min(0).max(86400),
  count: z.number().int().min(1).max(100000),
  firstAt: isoDateTime,
  lastAt: isoDateTime,
})
export type BypassEvent = z.infer<typeof BypassEventSchema>

/**
 * §5.3 — the pulse. Drives liveness, `server_stats`, config/ban-list version
 * sync and command delivery. `seq` is monotonic per `bootId` and persisted in
 * `cache/queue.json`; a replay is `409 HEARTBEAT_SEQ_REPLAY`, and a `bootId`
 * differing from the JWT `boot` claim forces re-auth.
 */
export const HeartbeatRequestSchema = z.object({
  seq: z.number().int().min(1).max(Number.MAX_SAFE_INTEGER),
  bootId,
  /** Seconds since this boot; capped at one year of continuous uptime. */
  uptime: z.number().int().min(0).max(31536000),
  playersOnline: z.number().int().min(0).max(2048),
  maxPlayers: z.number().int().min(0).max(2048),
  agentVersion: semver,
  resourceHash: z.string().max(64),
  resourceCount: z.number().int().min(0).max(5000),
  /** Mean server tick cost in ms; fractional by nature. */
  tickMs: z.number().min(0).max(60000),
  /** Live disk-queue depth, so the panel can show a backlog (§5.11). */
  queueDepth: z.number().int().min(0).max(1000000),
  /** What the agent has actually applied; `=== configVersion` lights "Sunucuya uygulandı ✓". */
  appliedConfigVersion: versionNumber,
  frameworkDetected: FrameworkSchema,
  /** Capped at 100 per beat; overflow is dropped oldest-first and logged locally. */
  bypassEvents: z.array(BypassEventSchema).max(100).default([]),
})
export type HeartbeatRequest = z.infer<typeof HeartbeatRequestSchema>

/**
 * §5.3 — `200` response, MAC'd. `configVersion > appliedConfigVersion` triggers
 * §5.4; a `banListVersion` above the cached one triggers §5.8.3.
 */
export const HeartbeatResponseSchema = z.object({
  serverTime: isoDateTime,
  configVersion: versionNumber,
  banListVersion: versionNumber,
  /** Server-driven pacing, never below 10 000; overrides `policy.heartbeatIntervalMs`. */
  retryAfterMs: z.number().int().min(10000).max(3600000),
  policy: AgentPolicySchema,
  /** At most 20 per heartbeat, ordered `created_at ASC`. */
  commands: z.array(AgentCommandSchema).max(20).default([]),
})
export type HeartbeatResponse = z.infer<typeof HeartbeatResponseSchema>

/* -------------------------------------------------------------------------- */
/* §5.5 POST /v1/agent/detections                                              */
/* -------------------------------------------------------------------------- */

/**
 * §5.5 / SPEC §8.6 — one detection as the agent built it. Detections are created
 * *only* here. `eventId` is the idempotency key behind `unique(server_id, event_id)`,
 * so a replayed batch inserts nothing and reports duplicates, never an error.
 */
export const DetectionEventSchema = z.object({
  eventId: agentEventId,
  detectionKey: DetectionKeySchema,
  side: SideSchema,
  severity: SeveritySchema,
  /** The action after the §1.2 ladder (`logOnly`, unconfirmed ban → kick). */
  actionTaken: ActionSchema,
  actionExecuted: z.boolean(),
  /** A server-side getter corroborated the client claim (`serverVerifiable` modules). */
  serverConfirmed: z.boolean().default(false),
  strikes: z.number().int().min(0).max(1000),
  score: z.number().int().min(0).max(100),
  playerSource,
  playerName,
  identifiers: IdentifiersSchema,
  tokens: TokensSchema.default([]),
  /** `GetEntityCoords(GetPlayerPed(src))` at report time; absent when there is no ped. */
  coords: CoordsSchema.nullable().default(null),
  /** Module-specific proof blob, opaque to the schema and size-capped. */
  evidence: evidenceObject,
  /** `shared/strike.lua` keeps at most 12 raw samples behind the verdict. */
  samples: z
    .array(z.record(z.string(), z.unknown()))
    .max(12)
    .refine(jsonSizeAtMost(MAX_SAMPLES_CHARS), { message: `samples exceed ${MAX_SAMPLES_CHARS} JSON chars` })
    .default([]),
  /** Asks for an inline `screenshotGrants[]` entry, subject to the config quotas. */
  screenshotRequested: z.boolean().default(false),
  /** Links this event to the ban minted in the same tick, in either arrival order. */
  agentBanCode: banCode.nullable().default(null),
  /** Agent clock corrected by `clockOffsetSec`; clamped server-side to ±5 min. */
  occurredAt: isoDateTime,
})
export type DetectionEvent = z.infer<typeof DetectionEventSchema>

/**
 * §5.5 — the batch envelope. The raw body is additionally capped at 256 KB by
 * the route (`413 BATCH_TOO_LARGE` with `details.maxBytes`), which no schema can
 * express; `events` carries the matching event-count half of that contract.
 */
export const DetectionBatchRequestSchema = z.object({
  /** `bootId .. '-b' .. n`; over 64 chars is a `400`. */
  batchId: z.string().min(1).max(64),
  events: z.array(DetectionEventSchema).min(1).max(100),
})
export type DetectionBatchRequest = z.infer<typeof DetectionBatchRequestSchema>

/**
 * §5.5 — per-event outcome. A rejection is permanent (the event is malformed and
 * will never succeed), so the agent drops it instead of re-queueing it.
 */
export const DetectionResultSchema = z.discriminatedUnion('status', [
  z.object({ eventId: agentEventId, status: z.literal('accepted'), detectionId: uuid }),
  z.object({ eventId: agentEventId, status: z.literal('duplicate'), detectionId: uuid }),
  z.object({
    eventId: agentEventId,
    status: z.literal('rejected'),
    code: ErrorCodeSchema,
    message: z.string().max(500),
  }),
])
export type DetectionResult = z.infer<typeof DetectionResultSchema>

/**
 * §5.5 — the inline capture path. One grant per accepted event that asked for a
 * screenshot and passed `screenshots.perPlayerCooldownMs` / `perServerPerMinute`;
 * grants that fail the quota are simply absent, never an error.
 */
export const ScreenshotGrantSchema = z.object({
  eventId: agentEventId,
  playerSource,
  screenshotId: uuid,
  /** Handed straight to `sentinel_ac:cl:screenshot`; must be reachable by players. */
  uploadUrl: httpUrl,
  expiresAt: isoDateTime,
})
export type ScreenshotGrant = z.infer<typeof ScreenshotGrantSchema>

/** §5.5 — `200` response (no MAC): counts, per-event results and screenshot grants. */
export const DetectionBatchResponseSchema = z.object({
  batchId: z.string().min(1).max(64),
  accepted: z.number().int().min(0).max(100),
  duplicates: z.number().int().min(0).max(100),
  rejected: z.number().int().min(0).max(100),
  results: z.array(DetectionResultSchema).max(100).default([]),
  screenshotGrants: z.array(ScreenshotGrantSchema).max(100).default([]),
})
export type DetectionBatchResponse = z.infer<typeof DetectionBatchResponseSchema>

/* -------------------------------------------------------------------------- */
/* §5.6 POST /v1/agent/connect                                                 */
/* -------------------------------------------------------------------------- */

/**
 * §5.6 / SPEC §8.8 — the `playerConnecting` verdict request, sent inside the
 * deferral under the agent's own `connect.timeoutMs` watchdog. A body with no
 * identifiers at all is `400 VALIDATION_ERROR`.
 */
export const ConnectCheckRequestSchema = z
  .object({
    playerSource,
    name: playerName,
    /** Bare values from `parseIdentifiers()`; `xbl`/`live` were removed by Cfx. */
    identifiers: IdentifiersSchema,
    /** Server-salted `GetPlayerTokens` strings — matched only within this server. */
    tokens: TokensSchema.default([]),
    /** `GetPlayerEndpoint(src)` with the port stripped, falling back to the `ip:` identifier. */
    ip: ipAddress.nullable().default(null),
    /** The agent's local ACE / `staffIdentifiers` result; recorded, never trusted. */
    isStaff: z.boolean().default(false),
  })
  .refine((body) => Object.keys(body.identifiers).length > 0, {
    message: 'at least one identifier is required',
    path: ['identifiers'],
  })
export type ConnectCheckRequest = z.infer<typeof ConnectCheckRequestSchema>

/**
 * §5.6 — `200` response, MAC'd. A rejection is a verdict, not an HTTP error, so
 * the status is always `200`. `reason` arrives already rendered from
 * `connect.messages.*` and goes straight to `deferrals.done(reason)`.
 */
export const ConnectCheckResponseSchema = z.object({
  allowed: z.boolean(),
  reason: z.string().max(1000).nullable().default(null),
  banCode: banCode.nullable().default(null),
  expiresAt: isoDateTime.nullable().default(null),
  appealUrl: appealUrl.nullable().default(null),
  playerId: uuid,
  trustScore: z.number().int().min(0).max(100),
  whitelisted: z.boolean().default(false),
  /** Empty for a full-bypass whitelist row (`modules: null`), which sets `whitelisted`. */
  whitelistModules: z.array(DetectionKeySchema).max(100).default([]),
  /** True whenever a global entry matched, even under `globalBans: 'flag'` (which allows). */
  globalMatch: z.boolean().default(false),
})
export type ConnectCheckResponse = z.infer<typeof ConnectCheckResponseSchema>

/* -------------------------------------------------------------------------- */
/* §5.7 POST /v1/agent/sessions                                                */
/* -------------------------------------------------------------------------- */

const sessionJoinEvent = z.object({
  /** `bootId .. '-s' .. n`; used for response correlation and agent-side dedupe only. */
  eventId: agentEventId,
  type: z.literal('join'),
  playerSource,
  playerName,
  identifiers: IdentifiersSchema,
  tokens: TokensSchema.default([]),
  ip: ipAddress.nullable().default(null),
  occurredAt: isoDateTime,
})

const sessionLeaveEvent = z.object({
  eventId: agentEventId,
  type: z.literal('leave'),
  playerSource,
  playerName,
  occurredAt: isoDateTime,
  durationSeconds: z.number().int().min(0).max(31536000),
  /** The `playerDropped` reason text. */
  dropReason: z.string().max(500).default(''),
  /** The resource that dropped the player; empty when none did. */
  resourceName: z.string().max(64).default(''),
  /** `ClientDropReason` (`RESOURCE=1` … `ONE_SYNC_TOO_MANY_MISSED_FRAMES=12`); 0 = unknown. */
  clientDropReason: z.number().int().min(0).max(12).default(0),
})

/**
 * §5.7 — a join or a leave. Idempotency is structural, not header-driven:
 * `player_sessions` has no `event_id` column, so a `join` upserts the open
 * session and a `leave` closes the newest open one (a no-op when already closed).
 */
export const SessionEventSchema = z.discriminatedUnion('type', [sessionJoinEvent, sessionLeaveEvent])
export type SessionEvent = z.infer<typeof SessionEventSchema>

/** §5.7 — flushed every `policy.sessionFlushMs`; over 200 events is `413 BATCH_TOO_LARGE`. */
export const SessionBatchRequestSchema = z.object({
  events: z.array(SessionEventSchema).min(1).max(200),
})
export type SessionBatchRequest = z.infer<typeof SessionBatchRequestSchema>

/** §5.7 — the echo that teaches the agent the cloud `playerId` for offline joins. */
export const SessionPlayerEchoSchema = z.object({
  eventId: agentEventId,
  playerSource,
  playerId: uuid,
  trustScore: z.number().int().min(0).max(100),
  whitelisted: z.boolean().default(false),
})
export type SessionPlayerEcho = z.infer<typeof SessionPlayerEchoSchema>

/** §5.7 — `200` response (no MAC). */
export const SessionBatchResponseSchema = z.object({
  accepted: z.number().int().min(0).max(200),
  duplicates: z.number().int().min(0).max(200),
  rejected: z.number().int().min(0).max(200),
  players: z.array(SessionPlayerEchoSchema).max(200).default([]),
})
export type SessionBatchResponse = z.infer<typeof SessionBatchResponseSchema>

/* -------------------------------------------------------------------------- */
/* §5.14 POST /v1/agent/positions                                              */
/* -------------------------------------------------------------------------- */

/**
 * One player's place in the world, for the live map.
 *
 * Deliberately NOT stored. Positions are the highest-volume and least durable
 * thing the agent knows — a busy server produces one of these per player every
 * few seconds — and a history of where somebody stood is a privacy liability
 * with no detection value. The cloud holds the latest snapshot per server in
 * memory, pushes it to whoever is watching, and forgets it.
 */
export const PlayerPositionSchema = z.object({
  playerSource: playerSource,
  name: playerName,
  /** World coordinates, rounded to a metre by the agent. */
  x: z.number().min(-20000).max(20000),
  y: z.number().min(-20000).max(20000),
  z: z.number().min(-2000).max(5000),
  /** Degrees, 0 = north. */
  heading: z.number().min(0).max(360).default(0),
  inVehicle: z.boolean().default(false),
  health: z.number().int().min(0).max(1000).default(0),
})
export type PlayerPosition = z.infer<typeof PlayerPositionSchema>

/** §5.14 — the whole online roster in one beat; a partial batch is not a thing. */
export const PositionsRequestSchema = z.object({
  players: z.array(PlayerPositionSchema).max(512).default([]),
})
export type PositionsRequest = z.infer<typeof PositionsRequestSchema>

export const PositionsResponseSchema = z.object({
  accepted: z.number().int().min(0).max(512),
  /** Server-driven pacing, so the cloud can slow a chatty agent down. */
  nextIntervalMs: z.number().int().min(1000).max(60000),
})
export type PositionsResponse = z.infer<typeof PositionsResponseSchema>

/* -------------------------------------------------------------------------- */
/* §5.8 Bans                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * `ban_source` values an agent may claim (SPEC §3.1). `panel` and `evasion` are
 * cloud-only and must be rejected here.
 */
export const AgentBanSourceSchema = z.enum(['system', 'export', 'command', 'import'])
export type AgentBanSource = z.infer<typeof AgentBanSourceSchema>

/**
 * §5.8.1 — reconciles a ban the agent has *already* executed offline. Idempotent
 * on `unique(server_id, agent_event_id)`, so the disk queue may replay it freely.
 * A body with no hard identifier is `400 VALIDATION_ERROR`.
 */
export const AgentBanRequestSchema = z
  .object({
    /** The agent's idempotency key for this ban (`bootId .. '-b' .. seq`). */
    agentEventId,
    /** Provisional `SAC-L…` code already shown to the player and cached locally. */
    agentBanCode: banCode,
    playerSource,
    playerName,
    reason: z.string().min(1).max(500),
    /** `DetectionEvent.eventId` of the trigger, so the cloud can back-fill both links. */
    detectionEventId: agentEventId.nullable().default(null),
    detectionKey: DetectionKeySchema.nullable().default(null),
    source: AgentBanSourceSchema,
    /** `SentinelAC`, the invoking resource name, or the console label. */
    issuedByLabel: z.string().min(1).max(120),
    identifiers: IdentifiersSchema,
    tokens: TokensSchema.default([]),
    ip: ipAddress.nullable().default(null),
    /** 0 = permanent; over one year is a `400`. */
    durationSec: z.number().int().min(0).max(31536000).default(0),
    screenshotId: uuid.nullable().default(null),
    occurredAt: isoDateTime,
  })
  .refine((body) => primaryIdentifier(body.identifiers) !== null, {
    message: 'at least one hard identifier is required',
    path: ['identifiers'],
  })
export type AgentBanRequest = z.infer<typeof AgentBanRequestSchema>

/** The `bans` row as the agent needs it back (SPEC §3.2). */
export const AgentBanRecordSchema = z.object({
  id: uuid,
  /** Canonical cloud code; the agent rewrites its cache entry from `agentBanCode` to this. */
  banCode,
  agentBanCode: banCode.nullable().default(null),
  playerId: uuid.nullable().default(null),
  reason: z.string().max(500),
  detectionId: uuid.nullable().default(null),
  expiresAt: isoDateTime.nullable().default(null),
  active: z.boolean(),
  isGlobal: z.boolean().default(false),
  /** `global_status` (SPEC §3.1). */
  globalStatus: z.enum(['none', 'proposed', 'approved', 'rejected', 'revoked']).default('none'),
  createdAt: isoDateTime,
})
export type AgentBanRecord = z.infer<typeof AgentBanRecordSchema>

/** §5.8.1 — `201`, or `200` with `duplicate: true` on an idempotent replay. MAC'd. */
export const AgentBanResponseSchema = z.object({
  ban: AgentBanRecordSchema,
  duplicate: z.boolean().default(false),
  /** Already bumped by this create; the agent stores it to skip a needless snapshot. */
  banListVersion: versionNumber,
})
export type AgentBanResponse = z.infer<typeof AgentBanResponseSchema>

/**
 * §5.8.2 — `DELETE /v1/agent/bans/:banCode` body. Both fields are optional: a
 * body on `DELETE` is not confirmed to survive `PerformHttpRequest` end to end,
 * so an empty body is valid (the API then records `reason: null`) and the same
 * two fields are accepted as query parameters.
 */
export const AgentUnbanRequestSchema = z.object({
  reason: z.string().max(500).nullish().default(null),
  issuedByLabel: z.string().max(120).nullish().default(null),
})
export type AgentUnbanRequest = z.infer<typeof AgentUnbanRequestSchema>

/** §5.8.2 — the query-parameter fallback for the same two fields. */
export const AgentUnbanQuerySchema = z.object({
  reason: z.string().max(500).optional(),
  issuedByLabel: z.string().max(120).optional(),
})
export type AgentUnbanQuery = z.infer<typeof AgentUnbanQuerySchema>

/** §5.8.2 — the inline revoke response (no MAC). */
export const AgentUnbanResponseSchema = z.object({
  banCode,
  banId: uuid,
  active: z.literal(false),
  revokedAt: isoDateTime,
  banListVersion: versionNumber,
})
export type AgentUnbanResponse = z.infer<typeof AgentUnbanResponseSchema>

/** §5.8.3 — `?since=&sinceAt=&cursor=`; omitting `since`/`sinceAt` requests a full snapshot. */
export const BanSnapshotQuerySchema = z.object({
  /** The cached `banListVersion`. */
  since: z.coerce.number().int().min(0).max(Number.MAX_SAFE_INTEGER).optional(),
  /** The `generatedAt` received with that version; missing or older than 7 days forces `full`. */
  sinceAt: isoDateTime.optional(),
  cursor: z.string().max(512).optional(),
})
export type BanSnapshotQuery = z.infer<typeof BanSnapshotQuerySchema>

/** §5.8.3 — a tenant-local ban, carrying plain identifier values (its own data). */
export const BanSnapshotLocalEntrySchema = z.object({
  banCode,
  reason: z.string().max(500).nullable().default(null),
  expiresAt: isoDateTime.nullable().default(null),
  active: z.boolean().default(true),
  identifiers: IdentifiersSchema,
  tokens: TokensSchema.default([]),
  ip: ipAddress.nullable().default(null),
})
export type BanSnapshotLocalEntry = z.infer<typeof BanSnapshotLocalEntrySchema>

/**
 * §5.8.3 — a global ban, carrying **only** `sha256(type .. ':' .. value)` of the
 * hard identifiers over lowercase unprefixed values (SPEC §10.7). The agent
 * hashes a joining player's identifiers the same way; plaintext never crosses tenants.
 */
export const BanSnapshotGlobalEntrySchema = z.object({
  banCode,
  expiresAt: isoDateTime.nullable().default(null),
  hashes: z.array(sha256Hex).max(8),
})
export type BanSnapshotGlobalEntry = z.infer<typeof BanSnapshotGlobalEntrySchema>

/**
 * §5.8.3 — `200` response, MAC'd. Pages cap at 5000 entries; a non-null
 * `nextCursor` must be followed before the new `banListVersion` is stored, so an
 * interrupted paging run simply repeats.
 */
export const BanSnapshotResponseSchema = z.object({
  banListVersion: versionNumber,
  since: versionNumber.nullable().default(null),
  /** True when the agent must replace `cache/bans.json` wholesale. */
  full: z.boolean(),
  generatedAt: isoDateTime,
  local: z.object({
    upserts: z.array(BanSnapshotLocalEntrySchema).max(5000).default([]),
    removals: z.array(banCode).max(5000).default([]),
  }),
  /** Populated only under `connect.globalBans = 'block'`; empty under `flag`/`off`. */
  global: z.object({
    mode: z.enum(['block', 'flag', 'off']),
    upserts: z.array(BanSnapshotGlobalEntrySchema).max(5000).default([]),
    removals: z.array(banCode).max(5000).default([]),
  }),
  counts: z.object({
    local: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
    global: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
  }),
  nextCursor: z.string().max(512).nullable().default(null),
})
export type BanSnapshotResponse = z.infer<typeof BanSnapshotResponseSchema>

/* -------------------------------------------------------------------------- */
/* §5.10 Screenshots                                                           */
/* -------------------------------------------------------------------------- */

/**
 * §5.10.1 — the out-of-band grant path (`RequestScreenshot`, `sac screenshot`,
 * or a panel `screenshot` command). Captures already granted inline by a
 * detection batch do not go through here.
 */
export const ScreenshotTokenRequestSchema = z.object({
  playerSource,
  playerName,
  identifiers: IdentifiersSchema,
  /** The detection this capture is evidence for, when there is one. */
  detectionEventId: agentEventId.nullable().default(null),
  /** The `screenshot` command being fulfilled, when the panel asked for it. */
  commandId: uuid.nullable().default(null),
})
export type ScreenshotTokenRequest = z.infer<typeof ScreenshotTokenRequestSchema>

/**
 * §5.10.1 — `201` response (no MAC). `encoding` and `quality` echo
 * `config.screenshots` so a capture is correct even when the agent's config is a
 * version behind.
 */
export const ScreenshotTokenResponseSchema = z.object({
  screenshotId: uuid,
  /** `POST /v1/uploads/:token` — the token in the path is the whole credential. */
  uploadUrl: httpUrl,
  expiresAt: isoDateTime,
  /** 120 s TTL, single atomic redemption (SPEC §10.4). */
  expiresInSec: z.number().int().min(1).max(3600),
  /** 5 MiB, enforced by `@fastify/multipart`. */
  maxBytes: z.number().int().min(1).max(5242880),
  encoding: z.enum(['jpg', 'png', 'webp']),
  quality: z.number().min(0.1).max(1),
})
export type ScreenshotTokenResponse = z.infer<typeof ScreenshotTokenResponseSchema>

/**
 * §5.10.2 — `POST /v1/uploads/:token` result. The agent never calls this route;
 * the upload comes from the player's NUI, which holds no agent secrets.
 */
export const UploadResponseSchema = z.object({
  ok: z.literal(true),
  screenshotId: uuid,
  bytes: z.number().int().min(1).max(5242880),
  width: z.number().int().min(1).max(16384),
  height: z.number().int().min(1).max(16384),
})
export type UploadResponse = z.infer<typeof UploadResponseSchema>
